// wwwroot/components/hero-video.js

const DEFAULTS = {
    heroSelector: "#Hero",
    minDownlinkMbps: 3,
    allowedEffectiveTypes: ["4g"],
    mobileQuery: "(max-width: 767px)",
    reducedMotionQuery: "(prefers-reduced-motion: reduce)",
    rootMargin: "200px 0px",
    desktopSelector: ".desktop-video",
    mobileSelector: ".mobile-video",
};

/** @returns {boolean} */
function connectionAllowsVideo({ minDownlinkMbps, allowedEffectiveTypes }) {
    const c = navigator.connection || navigator.webkitConnection || navigator.mozConnection;

    // Respect "Data Saver"
    if (c && c.saveData) return false;

    // If the API exists and provides useful info, enforce thresholds
    if (c && (typeof c.downlink === "number" || typeof c.effectiveType === "string")) {
        const okType = !c.effectiveType || allowedEffectiveTypes.includes(c.effectiveType);
        const okDownlink = !c.downlink || c.downlink >= minDownlinkMbps;
        return okType && okDownlink;
    }

    // Default allow if Network Information API not supported
    return true;
}

/** @param {HTMLVideoElement} videoEl */
function hydrateVideo(videoEl) {
    if (!videoEl || videoEl.dataset.hydrated) return;

    const sources = videoEl.querySelectorAll("source[data-src]");
    sources.forEach((s) => {
        s.src = s.getAttribute("data-src");
        s.removeAttribute("data-src");
    });

    videoEl.load();

    videoEl.addEventListener(
        "playing",
        () => {
            videoEl.classList.add("is-playing");
        },
        { once: true }
    );

    videoEl.dataset.hydrated = "true";
}

/**
 * Initializes the hero video lazy-hydration on pages that have the hero.
 * Safe to call on every page.
 *
 * @param {Partial<typeof DEFAULTS>} options
 * @returns {() => void} cleanup function
 */
export function initHeroVideo(options = {}) {
    const cfg = { ...DEFAULTS, ...options };

    const prefersReducedMotion = window.matchMedia(cfg.reducedMotionQuery);
    if (prefersReducedMotion.matches) return () => { };

    const hero = document.querySelector(cfg.heroSelector);
    if (!hero) return () => { };

    const isMobile = window.matchMedia(cfg.mobileQuery).matches;
    const targetVideo = hero.querySelector(isMobile ? cfg.mobileSelector : cfg.desktopSelector);
    if (!targetVideo) return () => { };

    if (!connectionAllowsVideo(cfg)) return () => { };

    // Older browsers: if no IO, just hydrate immediately.
    if (!("IntersectionObserver" in window)) {
        hydrateVideo(targetVideo);
        return () => { };
    }

    const io = new IntersectionObserver(
        (entries, obs) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    hydrateVideo(targetVideo);
                    obs.disconnect();
                    break;
                }
            }
        },
        { rootMargin: cfg.rootMargin }
    );

    io.observe(hero);

    // cleanup (handy if you ever re-init on partial page nav)
    return () => io.disconnect();
}
